import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  datetimeLocalToIso,
  formatSubscriberDateTime,
  isoToDatetimeLocal,
} from '../../lib/subscriberDates';
import {
  SUBSCRIBER_FUNNEL_STATUSES,
  subscriberFunnelStatusLabel,
} from '../../lib/subscriberFunnelStatus';

export default function SubscriberFunnelFieldsSection({
  subscriberId,
  funnelStatus,
  lastContactedAt,
  onSaved,
}: {
  subscriberId: string;
  funnelStatus: string | null | undefined;
  lastContactedAt: string | null | undefined;
  onSaved: () => void;
}) {
  const [funnelDraft, setFunnelDraft] = useState('');
  const [lastContactedDraft, setLastContactedDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setFunnelDraft(typeof funnelStatus === 'string' ? funnelStatus : '');
    setLastContactedDraft(isoToDatetimeLocal(lastContactedAt ?? null));
    setDirty(false);
  }, [funnelStatus, lastContactedAt, subscriberId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const lastContactedIso = datetimeLocalToIso(lastContactedDraft);
      const { error } = await supabase
        .from('subscribers')
        .update({
          funnel_status: funnelDraft.trim() || null,
          last_contacted_at: lastContactedIso,
          updated_at: new Date().toISOString(),
        })
        .eq('id', subscriberId);
      if (error) throw error;
      toast.success('Funnel fields saved');
      setDirty(false);
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
          Funnel
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 normal-case tracking-normal">
          CRM stage and last time you connected — separate from email subscription status.
        </p>
      </div>
      <div className="px-6 py-4 space-y-4">
        <div>
          <label
            htmlFor="subscriber-funnel-status"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Funnel status
          </label>
          <select
            id="subscriber-funnel-status"
            value={funnelDraft}
            onChange={(e) => {
              setFunnelDraft(e.target.value);
              setDirty(true);
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">Not set</option>
            {SUBSCRIBER_FUNNEL_STATUSES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          {funnelDraft && !SUBSCRIBER_FUNNEL_STATUSES.some((item) => item.value === funnelDraft) ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Current: {subscriberFunnelStatusLabel(funnelDraft)}
            </p>
          ) : null}
        </div>

        <div>
          <label
            htmlFor="subscriber-last-contacted"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Last connected
          </label>
          <input
            id="subscriber-last-contacted"
            type="datetime-local"
            value={lastContactedDraft}
            onChange={(e) => {
              setLastContactedDraft(e.target.value);
              setDirty(true);
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          {lastContactedAt ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Saved as {formatSubscriberDateTime(lastContactedAt)}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save funnel fields
          </button>
        </div>
      </div>
    </section>
  );
}
